import { create } from 'zustand';

export type OfflineToastType = 'queued' | 'offline-info' | 'sync-ok' | 'sync-fail';

export interface OfflineToastEntry {
  id: string;
  type: OfflineToastType;
  message: string;
  detail?: string;
  /** Auto-dismiss after ms (default 3500) */
  duration?: number;
  createdAt: number;
}

interface OfflineToastState {
  toasts: OfflineToastEntry[];
  push: (type: OfflineToastType, message: string, detail?: string, duration?: number) => void;
  dismiss: (id: string) => void;
}

let counter = 0;

export const useOfflineToastStore = create<OfflineToastState>((set) => ({
  toasts: [],
  push: (type, message, detail, duration) => {
    const id = `ot-${++counter}-${Date.now()}`;
    const entry: OfflineToastEntry = { id, type, message, detail, duration, createdAt: Date.now() };
    set((s) => ({ toasts: [...s.toasts, entry] }));
    // Auto-dismiss
    const ms = duration ?? 3500;
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, ms);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Shorthand for common offline toast patterns */
export const offlineToast = {
  queued: (message: string, detail?: string) =>
    useOfflineToastStore.getState().push('queued', message, detail),
  info: (message: string) =>
    useOfflineToastStore.getState().push('offline-info', message),
  syncOk: (message: string) =>
    useOfflineToastStore.getState().push('sync-ok', message, undefined, 2500),
  syncFail: (message: string, detail?: string) =>
    useOfflineToastStore.getState().push('sync-fail', message, detail, 5000),
};
